import { isValidSelectorString } from '@/utils/validations/selector.validation';
import { z } from 'zod';

const ReceiverContactSchema = z.object({
  id: z.string().optional(),
  lastName: z.string().optional(),
  firstName: z.string().optional(),
  email: z.string().email({ message: 'Email incorrect' }).optional(),
  phoneNumber: z
    .union([
      z
        .string()
        .regex(
          /^\+?[0-9]+$/,
          'Le numéro de téléphone ne doit contenir que des chiffres ou un + au début',
        )
        .min(5, 'Le numéro de téléphone doit contenir au moins 5 caractères'),
      z.string().max(0),
    ])
    .optional(),
});

export const ExtendedReceiverContactSchema = z.object({
  contacts: z.array(ReceiverContactSchema),
});

export type ExtendedReceiverContactsFormData = z.infer<
  typeof ExtendedReceiverContactSchema
>;

const reservationEmailsSchema = z.object({
  email: z.string().email({ message: 'Email incorrect' }).optional(),
});

export const ExtendedReceiverReservationEmailsSchema = z.object({
  reservationEmails: z
    .array(reservationEmailsSchema)
    .refine((emails) => emails[0]?.email !== undefined, {
      message: 'Au moins un email de réservation est requis',
      // On the first input, since nothing displays errors on the list itself.
      path: [0, 'email'],
    }),
});

export type ExtendedReceiverReservationEmailsFormData = z.infer<
  typeof ExtendedReceiverReservationEmailsSchema
>;

const ReceiverCompanySchema = ExtendedReceiverContactSchema.extend(
  ExtendedReceiverReservationEmailsSchema.shape,
).extend({
  name: z.string({ message: "Le nom de l'agence est requis" }),
  logo: z
    .union([
      z.instanceof(Blob, { message: 'Fichier incorrect ou inexistant' }),
      z.null(),
      z.string(),
    ])
    .optional(),
  address: z.string({ message: "L'adresse est requise" }).optional(),
  city: z.string({ message: 'La ville est requise' }).optional(),
  postalCode: z.coerce
    .number({ message: 'Le code postal est requis' })
    .optional(),
  country: isValidSelectorString('Le pays est requis'),
  currency: isValidSelectorString('La devise est requise'),
  website: z
    .union([
      z.string().url({ message: 'Le site web est invalide' }),
      z.string().max(0),
    ])
    .optional(),
});

export const ExtendedReceiverCompanySchema = z.object({
  company: ReceiverCompanySchema,
});

export type ExtendedReceiverCompanyFormData = z.infer<
  typeof ExtendedReceiverCompanySchema
>;

export const ReceiverProfileSchema = ExtendedReceiverCompanySchema.extend({
  firstname: z.string(),
  lastname: z.string(),
  email: z
    .string({
      required_error: "L'email est requis",
      invalid_type_error: 'Email incorrect',
    })
    .email(),
  password: z
    .string({
      required_error: 'Le mot de passe est requis',
    })
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .optional(),
  mainLanguage: isValidSelectorString('La langue principale est requise'),
});

export type ReceiverProfileFormData = z.infer<typeof ReceiverProfileSchema>;
